/**
 * Eligibility rules for PhotoLavie app releases (KAN-436). A PhotoLavie release (Release to All or selected devices) may only
 * write to active PhotoLavie devices: never the `global` placeholder, never soft-deleted devices (`isDeleted === true`; a missing
 * field means active) and never Konobox devices. The Konobox test is the same loose rule Photobooth Management uses to hide
 * Konobox (blueprint 3.1.2): brand name or id containing `konobox`, plus the literal `konobox` id, resolved from the device's
 * `brandId` and falling back to its store's `brandId`. PhotoLavie test machines (`isTesting`) stay eligible.
 */
export type AppReleaseSkipReason = 'global' | 'deleted' | 'konobox';

export interface AppReleaseTargets {
  eligibleIds: string[];
  skipped: { id: string; reason: AppReleaseSkipReason }[];
}

export function konoboxBrandIdSet(brands: any[]): Set<string> {
  return new Set(
    brands
      .filter(b => (b.name || '').toLowerCase().includes('konobox') || (b.id || '').toLowerCase().includes('konobox'))
      .map(b => b.id)
  );
}

/** `devices` and `stores` rows carry Firestore doc ids as `id`; `candidateIds` limits the check to a selection (default: all). */
export function partitionAppReleaseTargets(devices: any[], stores: any[], brands: any[], candidateIds?: string[]): AppReleaseTargets {
  const konoboxIds = konoboxBrandIdSet(brands);
  const storeMap = new Map<string, any>();
  stores.forEach(s => {
    if (s.id) storeMap.set(s.id, s);
    if (s.storeId) storeMap.set(s.storeId, s);
  });

  const byId = new Map<string, any>();
  devices.forEach(d => byId.set(d.id, d));
  const ids = candidateIds ?? devices.map(d => d.id);

  const result: AppReleaseTargets = { eligibleIds: [], skipped: [] };
  for (const id of ids) {
    const dev = byId.get(id);
    if (!dev) continue; // unknown id: nothing to write to
    if (id === 'global' || dev.deviceId === 'global') {
      result.skipped.push({ id, reason: 'global' });
    } else if (dev.isDeleted === true) {
      result.skipped.push({ id, reason: 'deleted' });
    } else {
      const brandId: string = dev.brandId || (dev.storeId ? storeMap.get(dev.storeId)?.brandId : '') || '';
      if (brandId && (konoboxIds.has(brandId) || brandId.toLowerCase() === 'konobox')) {
        result.skipped.push({ id, reason: 'konobox' });
      } else {
        result.eligibleIds.push(id);
      }
    }
  }
  return result;
}
